import React, { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useCatalog } from '../../hooks/useCatalog'
import EntryTypeBadge, { ENTRY_TYPE_LABELS } from '../../components/catalog/EntryTypeBadge'
import DimensionTag     from '../../components/catalog/DimensionTag'
import SourceProvenance from '../../components/catalog/SourceProvenance'

const LOW_CONFIDENCE_THRESHOLD = 0.85

function StatusBadge({ status }) {
  if (!status || status === 'published') return null
  const styles = {
    draft:    'bg-slate-100 text-slate-700',
    archived: 'bg-slate-200 text-slate-600',
    flagged:  'bg-rose-100 text-rose-700',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || styles.draft}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

export default function CatalogEntryDetail() {
  const { id } = useParams()
  const location = useLocation()
  const { fetchCatalogEntry, updateCatalogEntry } = useCatalog()
  const [entry, setEntry]   = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState(null)
  const [acting, setActing]     = useState(false)
  const [actionError, setActionError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    fetchCatalogEntry(id)
      .then((data) => { if (!cancelled) setEntry(data) })
      .catch((err) => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [id, fetchCatalogEntry])

  // Preserve whatever search params we had on /catalog so the back link
  // returns the user to the same filtered view. We grab them from the
  // location state when the browser navigated from /catalog, or from the
  // referrer URL as a fallback.
  const backToCatalogHref = location.state?.from || '/catalog'

  const changeStatus = async (nextStatus) => {
    setActing(true)
    setActionError(null)
    try {
      const updated = await updateCatalogEntry(id, { status: nextStatus })
      setEntry(updated)
    } catch (err) {
      setActionError(err?.message || 'Could not update the entry')
    } finally {
      setActing(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading catalog entry…</p>
  }

  if (error) {
    return (
      <div className="max-w-2xl">
        <Link to={backToCatalogHref} className="text-xs font-medium text-slate-500 hover:text-slate-700">
          ← Back to catalog
        </Link>
        <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      </div>
    )
  }

  if (!entry) return null

  const dimensions = entry.dimensions || {}
  const dimensionKeys = Object.keys(dimensions)
  const isLowConfidence = typeof entry.confidence === 'number' && entry.confidence < LOW_CONFIDENCE_THRESHOLD
  const confidencePct   = Math.round((entry.confidence || 0) * 100)
  const isPublished     = entry.status === 'published'
  // "Pending review" is a status concept (draft/flagged), NOT a confidence
  // one — a published entry is live regardless of its confidence score.
  const isPendingReview = entry.status === 'draft' || entry.status === 'flagged'
  const entryTypeLabel  = ENTRY_TYPE_LABELS[entry.entry_type] || entry.entry_type

  return (
    <div className="max-w-4xl mx-auto space-y-10">
      {/* Breadcrumb */}
      <nav className="text-xs text-slate-500">
        <Link to={backToCatalogHref} className="hover:text-slate-700">Catalog</Link>
        <span className="mx-2 text-slate-300">›</span>
        <span>{entryTypeLabel}</span>
        <span className="mx-2 text-slate-300">›</span>
        <span className="text-slate-700 truncate">{entry.title}</span>
      </nav>

      {/* Header */}
      <header className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 leading-tight">
            {entry.title}
          </h1>
          <div className="flex items-center gap-2">
            <EntryTypeBadge entryType={entry.entry_type} />
            <StatusBadge status={entry.status} />
          </div>
        </div>

        {/* Curation actions (admin-only surface; /catalog is admin-gated). */}
        <div className="flex flex-wrap items-center gap-2">
          {entry.status !== 'published' && (
            <button
              type="button"
              disabled={acting}
              onClick={() => changeStatus('published')}
              className="rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-500 disabled:opacity-50"
            >
              Publish
            </button>
          )}
          {entry.status !== 'archived' && (
            <button
              type="button"
              disabled={acting}
              onClick={() => changeStatus('archived')}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Archive
            </button>
          )}
          {isPublished && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
              Published
            </span>
          )}
          {entry.status === 'archived' && (
            <span className="text-xs text-slate-400">Archived entries are hidden from the default catalog.</span>
          )}
          {actionError && <span className="text-xs text-rose-600">{actionError}</span>}
        </div>

        {entry.summary && (
          <p className="text-base text-slate-700 leading-relaxed max-w-3xl">
            {entry.summary}
          </p>
        )}

        {isPendingReview && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
            This entry is pending review{isLowConfidence ? ` — confidence score is ${confidencePct}%` : ''}.
          </div>
        )}

        {isPublished && isLowConfidence && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
            Low confidence ({confidencePct}%) — this entry is published but was extracted with lower certainty.
          </div>
        )}
      </header>

      {/* Detail */}
      {entry.detail && (
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Detail</h2>
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {entry.detail}
          </div>
        </section>
      )}

      {/* Dimensions */}
      {dimensionKeys.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Dimensions</h2>
          <div className="space-y-3">
            {dimensionKeys.map((slug) => {
              const values = dimensions[slug] || []
              if (values.length === 0) return null
              const label = slug.replace(/_/g, ' ')
              return (
                <div key={slug} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500 w-28 shrink-0 capitalize">
                    {label}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {values.map((value) => (
                      <DimensionTag
                        key={`${slug}-${value.id}`}
                        dimensionSlug={slug}
                        value={value}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* People */}
      {entry.people && entry.people.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">People</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {entry.people.map((person) => (
              <div key={person.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-600">
                  {(person.name || '?').slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{person.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {person.title || '—'}
                    {person.company ? <span className="text-slate-400"> · {person.company}</span> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Initiatives */}
      {entry.initiatives && entry.initiatives.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Initiatives</h2>
          <div className="flex flex-wrap gap-2">
            {entry.initiatives.map((initiative) => (
              <Link
                key={initiative.id}
                to={`/initiatives/${initiative.slug}`}
                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:border-accent-300 hover:text-accent-700 transition"
              >
                {initiative.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Sources */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
          Sources ({entry.sources?.length || 0})
        </h2>
        <SourceProvenance sources={entry.sources} />
      </section>
    </div>
  )
}
